import { useCallback, useRef, useEffect } from 'react';

import { useOutsideClick } from '@frontend/components';

import { useCascaderState } from '../context';
import { useMenuStack } from '../hooks/useMenuStack';
import { InternalMenuWrapper } from '../subcomponents';
import type {
  CascaderOption,
  CascaderOptionGroup,
  CascaderSubmenu as CascaderSubmenuType,
} from '../types';
import { isSubmenu } from '../util';
import { CascaderSubmenu } from './CascaderSubmenu';

/**
 * CascaderMenu is responsible for organizing the cascading submenus of the Cascader,
 * deciding which submenu is the active menu, and rendering them in a stack according
 * to the cursor path.
 */
export const CascaderMenu = () => {
  const menuRef = useRef<HTMLDivElement>(null);
  const {
    getOptionsAtLevel,
    cursorPath,
    rootGroups,
    root,
    setCursorPath: onCursorPathChange,
    handleSelect: onSelect,
    onRequestClose,
  } = useCascaderState();
  const menuStack = useMenuStack(rootGroups, root);

  // Auto-scroll to the rightmost menu when a new submenu appears
  useEffect(() => {
    if (menuRef.current && menuStack.length > 0) {
      // Use a small delay to ensure the DOM has updated with the new submenu
      const timeoutId = setTimeout(() => {
        if (menuRef.current) {
          menuRef.current.scrollTo({
            left: menuRef.current.scrollWidth,
            behavior: 'smooth',
          });
        }
      }, 50);

      return () => clearTimeout(timeoutId);
    }
  }, [menuStack.length]);

  // Helper function to build the full path for a selected option
  const buildFullPath = useCallback(
    (option: CascaderOption, level: number): string[] => {
      const fullPath: string[] = [];

      // Add values from the cursor path up to the selected level
      for (let i = 0; i < level; i++) {
        const currentOptions = getOptionsAtLevel(i);
        const currentIndex = cursorPath[i];
        if (currentIndex >= 0 && currentIndex < currentOptions.length) {
          const currentOption = currentOptions[currentIndex];
          fullPath.push(currentOption.value);
        }
      }

      // Add the selected option's value
      fullPath.push(option.value);

      return fullPath;
    },
    [cursorPath, getOptionsAtLevel],
  );

  // Handle option selection from the menu
  const handleSelectOption = useCallback(
    (option: CascaderOption, level: number) => {
      const currentOptions = getOptionsAtLevel(level);
      const optionIndex = currentOptions.findIndex(
        (opt) => opt.value === option.value,
      );

      if (!isSubmenu(option)) {
        const fullPath = buildFullPath(option, level);
        onSelect(option, fullPath);
        onRequestClose?.();
      } else {
        // Update cursor path
        const newCursorPath = cursorPath.slice(0, level + 1);
        newCursorPath[level] = optionIndex;
        onCursorPathChange(newCursorPath);
      }
    },
    [
      cursorPath,
      buildFullPath,
      onSelect,
      onCursorPathChange,
      onRequestClose,
      getOptionsAtLevel,
    ],
  );

  useOutsideClick({
    ref: menuRef,
    handler: () => {
      onRequestClose?.();
    },
  });

  return (
    <InternalMenuWrapper menuRef={menuRef}>
      {menuStack.map(({ groups, level, index, isOpen, submenu }) => (
        <CascaderSubmenu
          key={`${submenu.value}-${level}-${index}-${groups
            .flatMap((g) => g.children)
            .map((o) => o.value)
            .join('-')}`}
          submenu={submenu as CascaderSubmenuType<CascaderOption>}
          groups={groups as CascaderOptionGroup<CascaderOption>[]}
          level={level}
          cursorPath={cursorPath}
          isOpen={isOpen}
          onSelectOption={handleSelectOption}
        />
      ))}
    </InternalMenuWrapper>
  );
};
